import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Heart } from "lucide-react";
import BrandHeader from "../components/BrandHeader";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_habit-profile/artifacts/ituvkzkc_LM_180718_JustineJeddy_0177-Edit_HIGHres.jpg";

export default function Welcome() {
  return (
    <div className="min-h-screen bg-brand-base">
      <BrandHeader />

      <main className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch max-w-7xl mx-auto pt-6 lg:pt-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 flex flex-col justify-center"
          >
            <div className="label-eyebrow flex items-center gap-3" data-testid="welcome-eyebrow">
              <span className="inline-block h-px w-10 bg-brand-primary" />
              Your Personal HER Aligned Roadmap
            </div>

            <h1 className="font-serif font-bold text-5xl sm:text-6xl lg:text-[64px] text-brand-ink mt-6 leading-[0.98]" data-testid="welcome-headline">
              Welcome to <span className="italic text-brand-primary font-bold">HER Aligned</span>,
              <br/>
              beautiful.
            </h1>
            <p className="font-serif italic text-2xl md:text-3xl text-brand-ink-muted mt-4">
              We&apos;re so happy you&apos;re here
              <Heart className="inline ml-2 -mt-1 text-brand-heart" fill="#E8B25C" strokeWidth={1.5} size={22}/>
            </p>

            <div className="mt-8 max-w-xl space-y-5 text-base md:text-lg leading-relaxed text-brand-ink">
              <p>
                Let&apos;s get you onboarded by creating your <strong>HER Aligned Personalised Roadmap</strong> based on where you are right now, your habits and, most importantly, your goals and what you want to create for yourself. So this time, you don&apos;t just get results. You&apos;ll be the woman who trusts herself to keep them for life 🙌🏽💛
              </p>
            </div>

            <ul className="mt-10 space-y-4 max-w-xl">
              <li className="flex items-start gap-3" data-testid="welcome-feature-1">
                <Sparkles size={18} strokeWidth={1.5} className="text-brand-primary mt-1 shrink-0" />
                <span className="text-brand-ink">Evidence-based targets tailored to you, your lifestyle and goals.</span>
              </li>
              <li className="flex items-start gap-3" data-testid="welcome-feature-2">
                <Heart size={18} strokeWidth={1.5} className="text-brand-primary mt-1 shrink-0" />
                <span className="text-brand-ink">A holistic approach (not just training and nutrition) designed specifically for you.</span>
              </li>
              <li className="flex items-start gap-3" data-testid="welcome-feature-3">
                <ShieldCheck size={18} strokeWidth={1.5} className="text-brand-primary mt-1 shrink-0" />
                <span className="text-brand-ink">Receive a downloadable PDF of your results to keep and use for guidance.</span>
              </li>
            </ul>

            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <Link to="/audit" className="btn-primary inline-flex items-center gap-2.5" data-testid="start-audit-btn">
                Start My Roadmap Session <ArrowRight size={18} strokeWidth={2} />
              </Link>
              <span className="text-sm text-brand-ink-muted">Takes about 6 minutes.</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative overflow-hidden rounded-3xl h-[520px] sm:h-[600px] lg:h-full min-h-[600px]">
              <img
                src={HERO_IMAGE}
                alt="Justine with HER Aligned clients"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B2B]/55 via-[#2B2B2B]/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="surface-card p-6 backdrop-blur-sm bg-white/92">
                  <div className="label-eyebrow">A note from Justine</div>
                  <p className="font-serif text-lg md:text-xl text-brand-ink mt-2 leading-snug italic">
                    &quot;This is the conversation we would have if we were sitting down face to face with a coffee in hand, to map out your personalised plan for you to reach your goals in the most effective, enjoyable and sustainable way.&quot;
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
